import React, { useEffect, useState } from "react";
import "./Customer.css";
import { AiOutlineEdit, AiOutlineUserAdd, AiOutlineSearch } from "react-icons/ai";
import { getCustomers, updateCustomer } from "../../services/customerService";
import CustomerRegistration from "./CustomerRegistration";
import EditCustomer from "./EditCustomer";
import CustomerDetails from "./CustomerDetails"; // ✅ Import CustomerDetails component

function CustomerList() {
    const [customers, setCustomers] = useState(() => []);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 5;
    const [editCustomer, setEditCustomer] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null); // ✅ Store selected customer for details popup

    useEffect(() => {
        fetchCustomers();
    }, []);

    const fetchCustomers = async () => {
        try {
            const data = await getCustomers();
            if (!Array.isArray(data)) throw new Error("Invalid API response");
            const sortedCustomers = [...data].sort((a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt));
            setCustomers(sortedCustomers);
        } catch (error) {
            console.error("Failed to fetch customers:", error);
            setCustomers([]); // Ensure `customers` is never undefined
        }
    };

    const handleEdit = (customer, e) => {
        e.stopPropagation();
        setEditCustomer({ ...customer });
    };

    const handleRowClick = (customer) => {
        setSelectedCustomer(customer); // ✅ Show customer details popup
    };

    const handleSaveEdit = async (updatedCustomer) => {
        try {
            await updateCustomer(updatedCustomer.nic, updatedCustomer);
            setCustomers(customers.map(c => (c.nic === updatedCustomer.nic ? updatedCustomer : c)));
            fetchCustomers();
        } catch (error) {
            console.error("Failed to update customer:", error);
        }
    };

    // ✅ Ensure the newly added customer appears first
    const handleCustomerAdded = (newCustomer) => {
        setCustomers((prevCustomers) => [newCustomer, ...prevCustomers]);
        setIsModalOpen(false);
    };

    const filteredCustomers = (customers || []).filter((customer) =>
            customer && Object.values(customer || {}).some(value =>
                String(value || "").toLowerCase().includes(searchQuery.toLowerCase())
            )
    );

    const totalPages = Math.ceil(filteredCustomers.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedCustomers = filteredCustomers.slice(startIndex, startIndex + rowsPerPage);

    return (
        <div className="content-wrapper">
            <div className="customer-list-container">
                <div className="header-actions">
                    <div className="search-bar">
                        <AiOutlineSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search customers..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                    <button className="add-customer-btn" onClick={() => setIsModalOpen(true)}>
                        <AiOutlineUserAdd className="add-icon" /> Add Customer
                    </button>
                </div>

                <div className="table-wrapper">
                    <table>
                        <thead>
                        <tr>
                            <th>NIC</th>
                            <th>First Name</th>
                            <th>Last Name</th>
                            <th>DOB</th>
                            <th>Gender</th>
                            <th>Address</th>
                            <th>Contact</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {paginatedCustomers.map((customer) => (
                            <tr key={customer.NIC} onClick={() => handleRowClick(customer)}> {/* ✅ Handle row click */}
                                <td>{customer.NIC}</td>
                                <td>{customer.FirstName}</td>
                                <td>{customer.LastName}</td>
                                <td>{new Date(customer.DOB).toISOString().split("T")[0]}</td>
                                <td>{customer.Gender}</td>
                                <td>{customer.Address}</td>
                                <td>{customer.ContactNumber}</td>
                                <td className="actions">
                                    <button className="edit-customer-btn" onClick={(e) => handleEdit(customer, e)}>
                                        Edit Customer
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="pagination">
                        <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1}>Prev</button>
                        <span>Page {currentPage} of {totalPages}</span>
                        <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages}>Next</button>
                    </div>
                )}
            </div>

            <CustomerRegistration isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onCustomerAdded={handleCustomerAdded} />

            {editCustomer && (
                <EditCustomer
                    isOpen={!!editCustomer}
                    customer={editCustomer}
                    onClose={() => setEditCustomer(null)}
                    onSave={handleSaveEdit}
                />
            )}

            {selectedCustomer && (
                <CustomerDetails
                    isOpen={!!selectedCustomer}
                    customer={selectedCustomer}
                    onClose={() => setSelectedCustomer(null)} // ✅ Close details popup
                />
            )}
        </div>
    );
}

export default CustomerList;
